import React, { useState } from "react";
import { useDispatch } from "react-redux";
import SMBagActions from "actions/sm-bag-actions";
import RtrButton from "components/design-system/rtr-button";
import { openNotifyMeModal } from "slices/notify-me-slice";
import NotifyBellWhite from "images/notify_bell_white.svg";
import { useAppSelector } from "../../../../../../../util/store-types";
import MembershipHelpers, { isSubscriptionMember, hasExtraSpot } from "helpers/membership-helpers";
import { ctaText, ATB_ERROR_ID } from "rtr-constants";
import { display_membership_pdp_form } from "../../helpers/pixel-fires";
import { useOnLoadFlagOrExperiment } from "hooks/use-flags-and-experiments";
import flagsAndExperimentsNames from "constants/flags-and-experiments-names";
import HeapHelpers from "helpers/heap-helpers";

type Address = {
  postalCode: string;
  default: boolean;
};

type Sku = {
  canonicalSize: string[];
};

type SelectedSize = {
  sku: Sku;
  sizeDisplayText: string;
};

type StaticProduct = {
  productId: string;
  msrp: number;
};
type MembershipError = {
  text: string;
  ctaText?: string;
  errorURL?: string;
  additionalClassName?: string;
  type: string;
  id?: string;
};

type SubscriptionAddToBagButtonProps = {
  addresses: Address[];
  selectedSize: SelectedSize | null;
  staticProduct: StaticProduct;
  styleUnavailable: boolean;
  selectSizeIsUnavailable: boolean;
  availabilityLoaded?: boolean;
  membershipError?: MembershipError | null;
};

const SubscriptionAddToBagButton: React.FC<SubscriptionAddToBagButtonProps> = ({
  addresses,
  selectedSize,
  staticProduct,
  styleUnavailable,
  selectSizeIsUnavailable,
  availabilityLoaded,
  membershipError,
}) => {
  const dispatch = useDispatch();
  const [addingToBag, setAddingToBag] = useState(false);
  const isBagLoading = (useAppSelector(state => state.smBag?.smBagLoading) && addingToBag) || false;
  const userData = useAppSelector(state => state.userData);
  const membershipState = useAppSelector(state => state.membershipState);
  const ceBagCount = useAppSelector(state => state.ceBagCount);
  const isUpgradeRequired = membershipError?.id === ATB_ERROR_ID.INELIGBLE_ITEM_DUE_TO_BASIC_PLAN;
  const proratedAddonPrice = useAppSelector(state => state.proratedPrice?.addonPrice);
  const disabled =
    isBagLoading ||
    (!!membershipError && !isUpgradeRequired) ||
    (!selectedSize && !isUpgradeRequired) ||
    ((styleUnavailable || selectSizeIsUnavailable) && !isUpgradeRequired);

  const [addonPriceFF] = useOnLoadFlagOrExperiment(
    flagsAndExperimentsNames.SWAT_1162_PRORATED_ADDON_PRICE_FEATURE_TOGGLE
  );
  // Fire display_membership_pdp_form on mount
  React.useEffect(() => {
    display_membership_pdp_form(staticProduct?.productId);
  }, [staticProduct?.productId]);

  const addToBag = () => {
    const defaultAddress = addresses?.find(address => address?.default === true);
    setAddingToBag(true);

    const handleAddToMembershipBagSuccess = () => {
      setAddingToBag(false);
    };
    const handleAddToMembershipBagFailure = () => {
      setAddingToBag(false);
    };

    const heapData = {
      is_prorated_addon_treatment: addonPriceFF,
      addon_price: proratedAddonPrice,
      is_addon: MembershipHelpers.isBagFullCe(membershipState, ceBagCount),
    };

    HeapHelpers.fireHeapEvent("sf-pdp-atc-membership", heapData);

    const addSubscriptionItemToBag = SMBagActions.addBagItemPromise(
      {
        sku: selectedSize?.sku,
        type: "members",
        zipCode: defaultAddress?.postalCode,
      },
      handleAddToMembershipBagSuccess,
      handleAddToMembershipBagFailure,
      { productId: staticProduct.productId, retailPriceInteger: staticProduct.msrp }
    );
    dispatch(addSubscriptionItemToBag);
  };

  const canRenderNotifyMe =
    isSubscriptionMember(userData) &&
    availabilityLoaded &&
    (styleUnavailable || selectSizeIsUnavailable) &&
    (!membershipError ||
      membershipError?.type === "swap_notification" ||
      membershipError?.id === ATB_ERROR_ID.FUTURE_MEMBER_CANCEL ||
      membershipError?.id === ATB_ERROR_ID.FUTURE_MEMBER_PAUSED ||
      membershipError?.id === ATB_ERROR_ID.MEMBER_PAUSED ||
      membershipError?.id === ATB_ERROR_ID.MEMBER_OVERDUE_OR_PAYMENT_RETRY);

  const extraSpotPrice = () => {
    const addonPrice = proratedAddonPrice && addonPriceFF ? proratedAddonPrice : null;
    const baseSlotUpgradePrice = hasExtraSpot(membershipState, ceBagCount, null);
    if (!baseSlotUpgradePrice) return "";

    const addonPriceDisplay = hasExtraSpot(membershipState, ceBagCount, addonPrice);

    if (addonPriceDisplay && addonPriceDisplay !== baseSlotUpgradePrice) {
      return (
        <>
          {" | "}
          <del>{baseSlotUpgradePrice}</del> {addonPriceDisplay}
        </>
      );
    }

    return ` | ${baseSlotUpgradePrice}`;
  };

  const handleOnClick = () => {
    if (isUpgradeRequired && membershipError?.errorURL) {
      window.location.href = membershipError.errorURL;
    } else {
      addToBag();
    }
  };

  if (canRenderNotifyMe) {
    return (
      <RtrButton
        style="primary"
        className="my-4"
        dataTestId="universal-notify-me-atb"
        dataHeapId="universal-atb"
        onClick={() => dispatch(openNotifyMeModal(selectedSize?.sizeDisplayText))}
        fullWidth>
        <NotifyBellWhite /> notify me
      </RtrButton>
    );
  }

  return (
    <RtrButton
      style="primary"
      className="my-4 normal-case"
      dataTestId="updp-membership-atb"
      dataHeapId="updp-membership-atb"
      loading={isBagLoading}
      disabled={disabled}
      onClick={handleOnClick}
      fullWidth>
      {isUpgradeRequired ? (
        "Upgrade Your Plan"
      ) : isBagLoading ? (
        ctaText.ADDING
      ) : styleUnavailable || selectSizeIsUnavailable ? (
        ctaText.UNAVAILABLE
      ) : (
        <>
          {ctaText.ADD_TO_BAG}
          {extraSpotPrice()}
        </>
      )}
    </RtrButton>
  );
};

export default SubscriptionAddToBagButton;
